/**
 * MAYAS shared add-to-cart + pads cart check (2026-09-24, pads rule).
 *
 * RULE: every garment goes to the cart with its own pads, read from the garment
 * variant's metafields custom.pad_standard / pad_light / pad_super (empty slots skipped).
 * One pad of each type per garment unit. The legacy bundle products
 * (alle-pads-typer, alle-pads-typer-l-xl) are never added from here.
 * Pad prices are never shown or set here: the automatic "Free pads" discount prices them.
 *
 * 1) window.themeAddToCart(variantId, opts)          -> Promise<cart/add.js response>
 *    window.themeAddToCart(variantId, source, opts)  (form used by the wishlist page)
 *      opts.quantity  garment units (default 1); each pad line gets the same quantity
 *      opts.source    free text for debugging ("pdp_main", "quick_add", ...)
 *      opts.pads      pad variant ids, when the caller pre-rendered them (alias: padIds)
 *      opts.handle    product handle, used to look the pads up when opts.pads is absent
 *      opts.extra     more lines for the SAME call, e.g. [{id, quantity}] (v3 BOGO item)
 *      opts.open      false = do not open the cart drawer (default true)
 *    ONE /cart/add.js items[] call = garment + its pads (+ extra). If that call is
 *    rejected (e.g. a pad sold out between page load and tap), it retries without pads
 *    so the garment still sells, and the cart check fills in the pads it can.
 *    Fires no tracking: atc_clicked etc. belong to the calling surface.
 *
 * 2) Cart check: after any cart write on the page (fetch or XHR to /cart/add|change|
 *    update|clear) and on page load, adds MISSING pads ONCE for garments that reached the
 *    cart another way (Kaching widget, Swym, Prestige quick-buy, v2 PDP...).
 *    Memory per cart token in localStorage ("mayas_pads_v1"):
 *      s = garment units already given pads (or already checked) per variant id
 *      m = the pad ids each of those garments was given
 *      p = pending units recorded by themeAddToCart, folded into s on the next check
 *    A pad the customer removes is never re-added: the garment is already in s.
 *    No loops: s is saved BEFORE the check's own add, the check ignores its own
 *    requests, and it never runs while a themeAddToCart call is in flight.
 *    Garment quantity raised later (drawer +, Kaching "buy 2") = the new units get a set.
 *    Garment quantity lowered or garment removed = one set per removed unit is taken out
 *    again (FOLLOW_GARMENT_DOWN), so no orphan pads are left to be charged. Pads the
 *    customer already removed are not "owed" back. Adding the garment again = new set.
 *    No localStorage (private mode that throws) = the check stays off; themeAddToCart
 *    still adds the pads in its own call.
 *
 * Pad lookup: pre-rendered <script type="application/json" data-theme-pad-map> on the
 * page, else the Section Rendering API (sections/mayas-pad-map.liquid) by handle or by
 * cart. Cached per session.
 */
(function () {
  'use strict';

  if (window.themeAddToCart && window.themeAddToCart.__mayasPads) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var MEM_KEY = 'mayas_pads_v1';
  var MAP_KEY = 'mayas_pad_map_v1';
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  // Pads follow the garment down: garment quantity lowered or garment removed = the pads it
  // got from us are removed too (once). false = pads always stay once added.
  var FOLLOW_GARMENT_DOWN = true;

  var busy = 0;           // themeAddToCart calls in flight
  var selfWrite = 0;      // the check's own writes in flight
  var checkTimer = null;
  var checkRunning = false;
  var checkAgain = false;
  var memFallback = null; // used when localStorage throws
  var mapMem = {};        // variant id -> {pads:[], bundle:id|null}

  /* ---------- storage (every access guarded) ---------- */
  function readJSON(store, key) {
    try { var raw = window[store].getItem(key); return raw ? JSON.parse(raw) : null; } catch (e) { return undefined; }
  }
  function writeJSON(store, key, val) {
    try { window[store].setItem(key, JSON.stringify(val)); return true; } catch (e) { return false; }
  }

  function loadMem() {
    var m = readJSON('localStorage', MEM_KEY);
    if (m === undefined) m = memFallback;
    if (!m || typeof m !== 'object') m = {};
    if (!m.s || typeof m.s !== 'object') m.s = {};
    if (!m.p || typeof m.p !== 'object') m.p = {};
    if (!m.m || typeof m.m !== 'object') m.m = {};
    return m;
  }
  function saveMem(m) {
    memFallback = m;
    writeJSON('localStorage', MEM_KEY, m);
  }

  function addPending(variantId, qty) {
    var m = loadMem();
    var k = String(variantId);
    m.p[k] = (Number(m.p[k]) || 0) + qty;
    saveMem(m);
  }

  var STORE_OK = writeJSON('localStorage', 'mayas_pads_probe', 1);

  /* ---------- pad map ---------- */
  (function hydrateMapFromSession() {
    var cached = readJSON('sessionStorage', MAP_KEY);
    if (cached && typeof cached === 'object') mapMem = cached;
  })();

  function mergeMap(obj) {
    if (!obj || typeof obj !== 'object') return;
    Object.keys(obj).forEach(function (k) {
      var v = obj[k] || {};
      mapMem[String(k)] = {
        pads: (v.pads || []).filter(Boolean).map(String),
        bundle: v.bundle ? String(v.bundle) : null
      };
    });
    writeJSON('sessionStorage', MAP_KEY, mapMem);
  }

  function scanPageMaps(root) {
    var nodes = (root || document).querySelectorAll('script[data-theme-pad-map]');
    for (var i = 0; i < nodes.length; i++) {
      try { mergeMap(JSON.parse(nodes[i].textContent || '{}')); } catch (e) { /* ignore bad block */ }
    }
  }

  function fetchMap(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('pad map ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        scanPageMaps(doc);
      });
  }

  function mapFor(variantId) {
    return mapMem[String(variantId)] || null;
  }

  /* Pad variant ids for one garment variant. Resolves [] when unknown. */
  function padsFor(variantId, handle) {
    var hit = mapFor(variantId);
    if (hit) return Promise.resolve(hit.pads.slice());
    scanPageMaps();
    hit = mapFor(variantId);
    if (hit) return Promise.resolve(hit.pads.slice());
    if (!handle) return Promise.resolve(null);
    return fetchMap(ROOT + 'products/' + encodeURIComponent(handle) + '?section_id=mayas-pad-map')
      .then(function () {
        var h = mapFor(variantId);
        if (!h) mapMem[String(variantId)] = { pads: [], bundle: null };
        return (mapFor(variantId).pads || []).slice();
      })
      .catch(function () { return null; });
  }

  /* ---------- cart helpers ---------- */
  function postAdd(items) {
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) { var err = new Error((j && (j.description || j.message)) || 'add failed'); err.status = r.status; err.body = j; throw err; }
        return j;
      });
    });
  }

  function refreshCart(source) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: source || 'theme-add-to-cart' } }));
  }

  function openCart() {
    var drawer = document.getElementById('cart-drawer');
    var trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) { trigger.click(); return; }
    if (drawer && typeof drawer.show === 'function') { try { drawer.show(); return; } catch (e) { /* fall through */ } }
    window.location.href = ROOT + 'cart';
  }

  /* ---------- 1) shared add-to-cart ---------- */
  function themeAddToCart(variantId, a, b) {
    var opts = {};
    if (typeof a === 'string') { opts = b || {}; opts.source = opts.source || a; }
    else { opts = a || {}; }
    if (!variantId) return Promise.reject(new Error('no-variant'));

    var qty = Math.max(1, parseInt(opts.quantity, 10) || 1);
    var given = opts.pads || opts.padIds;
    var padsPromise = Array.isArray(given)
      ? Promise.resolve(given.filter(Boolean).map(String))
      : padsFor(variantId, opts.handle);

    busy++;
    return padsPromise.then(function (pads) {
      var known = Array.isArray(pads);
      var uniq = [];
      (pads || []).forEach(function (id) { if (uniq.indexOf(String(id)) < 0 && String(id) !== String(variantId)) uniq.push(String(id)); });

      var garment = [{ id: Number(variantId), quantity: qty }];
      var padLines = uniq.map(function (id) { return { id: Number(id), quantity: qty }; });
      var extra = (opts.extra || []).filter(function (x) { return x && x.id; }).map(function (x) {
        return { id: Number(x.id), quantity: Math.max(1, parseInt(x.quantity, 10) || 1) };
      });

      return postAdd(garment.concat(padLines, extra))
        .then(function (data) {
          // Pads handled here: the cart check must not add them again.
          if (known) addPending(variantId, qty);
          return data;
        })
        .catch(function (err) {
          if (!padLines.length || err.status !== 422) throw err;
          // A pad could not be added (sold out, limit). Sell the garment; the cart check
          // then adds whichever pads it still can.
          return postAdd(garment.concat(extra));
        });
    }).then(function (data) {
      busy--;
      refreshCart(opts.source);
      if (opts.open !== false) openCart();
      scheduleCheck(600);
      return data;
    }, function (err) {
      busy--;
      throw err;
    });
  }
  themeAddToCart.__mayasPads = 1;
  themeAddToCart.padsFor = padsFor;
  window.themeAddToCart = themeAddToCart;

  /* ---------- 2) cart check ---------- */
  function scheduleCheck(delay) {
    if (checkTimer) clearTimeout(checkTimer);
    checkTimer = setTimeout(runCheck, typeof delay === 'number' ? delay : 350);
  }

  function runCheck() {
    checkTimer = null;
    // Without localStorage the check cannot remember what the customer removed, so it
    // stays off (themeAddToCart still adds the pads in its own single call).
    if (!STORE_OK) return;
    if (busy > 0 || selfWrite > 0) { scheduleCheck(500); return; }
    if (checkRunning) { checkAgain = true; return; }
    checkRunning = true;
    doCheck().catch(function (e) {
      try { console.warn('[pads check]', e); } catch (x) { /* noop */ }
    }).then(function () {
      checkRunning = false;
      if (checkAgain) { checkAgain = false; scheduleCheck(350); }
    });
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }

  function postUpdate(updates) {
    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { if (!r.ok) throw new Error('update ' + r.status); return r.json(); });
  }

  function doCheck() {
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var mem = loadMem();
      if (mem.t !== cart.token) { mem.t = cart.token; mem.s = {}; mem.m = {}; }

      // Fold pending units from themeAddToCart into "served".
      Object.keys(mem.p).forEach(function (k) { mem.s[k] = (Number(mem.s[k]) || 0) + (Number(mem.p[k]) || 0); });
      mem.p = {};

      var qty = {};
      var lines = {};
      items.forEach(function (it) {
        var k = String(it.variant_id);
        qty[k] = (qty[k] || 0) + (Number(it.quantity) || 0);
        (lines[k] = lines[k] || []).push({ key: it.key, quantity: Number(it.quantity) || 0 });
      });

      var unknown = Object.keys(qty).filter(function (k) { return !mapFor(k); });
      var ready = unknown.length
        ? fetchMap(ROOT + 'cart?section_id=mayas-pad-map').then(function () {
            unknown.forEach(function (k) { if (!mapFor(k)) mapMem[k] = { pads: [], bundle: null }; });
          }).catch(function () { /* map unavailable: treat unknown as no pads this round */ })
        : Promise.resolve();

      return ready.then(function () {
        // 1) A garment that went DOWN (drawer minus, or removed): its pads follow, once.
        //    Never below zero, never touching pads the garment did not get from us.
        var reduce = {};
        Object.keys(mem.s).forEach(function (g) {
          var s = Number(mem.s[g]) || 0;
          var q = qty[g] || 0;
          if (q < s) {
            var mp = (mapFor(g) || {}).pads || mem.m[g] || [];
            if (FOLLOW_GARMENT_DOWN && mp.length) {
              mp.forEach(function (p) { reduce[p] = (reduce[p] || 0) + (s - q); });
            }
            mem.s[g] = q;
          }
          if (!q) { delete mem.s[g]; delete mem.m[g]; }
        });
        // 2) A garment that went UP or arrived another way: add the pads it is missing.
        var servedNeed = {};
        var unservedNeed = {};
        var cover = {};
        var garments = Object.keys(qty).filter(function (k) { var m = mapFor(k); return m && m.pads.length; });

        garments.forEach(function (g) {
          var m = mapFor(g);
          var q = qty[g];
          var served = Math.min(Number(mem.s[g]) || 0, q);
          var unserved = q - served;
          m.pads.forEach(function (p) {
            servedNeed[p] = (servedNeed[p] || 0) + served;
            unservedNeed[p] = (unservedNeed[p] || 0) + unserved;
            // A legacy pad bundle already in the cart counts as this garment's pads.
            if (m.bundle && qty[m.bundle]) cover[p] = (cover[p] || 0) + qty[m.bundle];
          });
          mem.s[g] = Math.max(Number(mem.s[g]) || 0, q);
          mem.m[g] = m.pads.slice();
        });

        // Cut only what no remaining garment still holds: another garment's pad of the
        // same type is never taken.
        var cutBy = {};
        var present = {};
        Object.keys(reduce).forEach(function (p) {
          cutBy[p] = Math.min(reduce[p], Math.max(0, (qty[p] || 0) - (servedNeed[p] || 0)));
        });
        Object.keys(qty).forEach(function (k) { present[k] = Math.max(0, qty[k] - (cutBy[k] || 0)); });

        var add = [];
        Object.keys(unservedNeed).forEach(function (p) {
          if (!unservedNeed[p]) return;
          // Pads already in the cart are never added again (a garment that came with one
          // pad, e.g. from the v2 PDP, only gets the types it lacks).
          var surplus = Math.max(0, (present[p] || 0) + (cover[p] || 0) - (servedNeed[p] || 0));
          var missing = Math.max(0, unservedNeed[p] - surplus);
          if (missing > 0) add.push({ id: Number(p), quantity: missing });
        });

        var updates = {};
        var hasUpdates = false;
        Object.keys(cutBy).forEach(function (p) {
          var cut = cutBy[p];
          (lines[p] || []).slice().reverse().forEach(function (ln) {
            if (cut <= 0) return;
            var take = Math.min(cut, ln.quantity);
            updates[ln.key] = ln.quantity - take;
            cut -= take;
            hasUpdates = true;
          });
        });

        // Remember BEFORE writing, so our own write can never trigger a second change.
        saveMem(mem);
        if (!add.length && !hasUpdates) return null;

        selfWrite++;
        var chain = hasUpdates ? postUpdate(updates).catch(function () { return null; }) : Promise.resolve();
        if (add.length) {
          chain = chain.then(function () {
            return postAdd(add).catch(function (err) {
              if (add.length < 2) throw err;
              // One pad failed: add the others one by one.
              return add.reduce(function (c, line) {
                return c.then(function () { return postAdd([line]).catch(function () { return null; }); });
              }, Promise.resolve());
            });
          });
        }
        return chain.then(function () {
          selfWrite--;
          refreshCart('pads-check');
        }, function (err) {
          selfWrite--;
          throw err;
        });
      });
    });
  }

  /* Watch cart writes made by anyone on the page (theme, Kaching, Swym, other apps). */
  function isCartWrite(url) {
    try { return CART_WRITE.test(new URL(String(url), window.location.href).pathname); } catch (e) { return false; }
  }

  if (window.fetch && !window.fetch.__mayasPads) {
    var origFetch = window.fetch;
    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var watch = isCartWrite(url) && selfWrite === 0;
      var p = origFetch.apply(this, arguments);
      if (watch) p.then(function () { scheduleCheck(); }, function () { /* ignore */ });
      return p;
    };
    wrapped.__mayasPads = 1;
    window.fetch = wrapped;
  }

  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__mayasPads) {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__mayasCartWrite = isCartWrite(url);
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__mayasCartWrite && selfWrite === 0) {
        this.addEventListener('loadend', function () { scheduleCheck(); });
      }
      return origSend.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__mayasPads = 1;
  }

  // Prestige product forms and quick-buy dispatch these after their own adds.
  document.addEventListener('variant:add', function () { scheduleCheck(); });
  document.addEventListener('cart:change', function () { scheduleCheck(); });
  // Apps that add with a fetch captured before this file loaded still ask the theme to refresh.
  document.addEventListener('cart:refresh', function () { scheduleCheck(); });

  // Page load: catches adds that navigated (form posts to /cart/add, app redirects).
  // Skipped when the page was rendered with an empty cart (window.__mayasCartCount = 0).
  function loadCheck() {
    if (window.__mayasCartCount === 0 && !Object.keys(loadMem().p).length) return;
    scheduleCheck(1200);
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) loadCheck(); });
  loadCheck();
})();
